import { useEffect, useMemo, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { PeoplePicker, type PeoplePickerProfile } from '@/components/projects/PeoplePicker';
import { CategoryGrid } from '@/components/tickets/NewTicketDialog';
import { useProfiles } from '@/features/users/hooks';
import { useTicketCategories } from '@/features/tickets/hooks';
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_META,
  describeTicketError,
  ticketRef,
} from '@/features/tickets/model';
import {
  buildTaskRequestDescription,
  useSendFeedbackTaskRequest,
  type FeedbackTaskWithContext,
  type FeedbackWithContext,
} from '@/features/customerFeedback/hooks';
import { FEEDBACK_TASK_KIND_META, feedbackCustomerLabel } from '@/features/customerFeedback/model';
import type { CustomerFeedbackTaskKind, TicketPriority } from '@/types/database';

interface SendTaskRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: FeedbackTaskWithContext | null;
  entry: FeedbackWithContext | null;
  onSent?: (ticketId: string) => void;
}

/**
 * Fallback routing when the model did not pick a ticket category: map
 * the kind of ask onto the seeded category slugs (migration 143) so the
 * request still lands with the right team.
 */
const KIND_TO_CATEGORY_SLUG: Record<CustomerFeedbackTaskKind, string | null> = {
  customization: 'prototype',
  product_idea: 'idea',
  customer_service: 'other',
  quality_issue: 'maintenance',
  sales_followup: 'other',
  other: 'other',
};

/**
 * "Send request" — review the extracted task, pick who should receive
 * it, and file it as a ticket. Everything is editable: the model drafts,
 * the rep decides.
 */
export function SendTaskRequestDialog({
  open,
  onOpenChange,
  task,
  entry,
  onSent,
}: SendTaskRequestDialogProps) {
  const categoriesQ = useTicketCategories();
  const profilesQ = useProfiles();
  const send = useSendFeedbackTaskRequest();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // `undefined` = the rep has not touched the category yet, so the
  // default is derived below (and still resolves once categories load).
  const [categoryChoice, setCategoryChoice] = useState<string | null | undefined>(undefined);
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [neededBy, setNeededBy] = useState('');
  const [sendTo, setSendTo] = useState<string[]>([]);

  const categories = useMemo(() => categoriesQ.data ?? [], [categoriesQ.data]);

  useEffect(() => {
    if (!open || !task || !entry) return;
    setTitle(task.title);
    setDescription(buildTaskRequestDescription(task, entry));
    setPriority(task.suggested_priority);
    setNeededBy('');
    setSendTo([]);
    setCategoryChoice(undefined);
  }, [open, task, entry]);

  const categoryId = useMemo(() => {
    if (categoryChoice !== undefined) return categoryChoice;
    if (!task) return null;
    if (task.suggested_category_id) return task.suggested_category_id;
    const fallbackSlug = KIND_TO_CATEGORY_SLUG[task.kind];
    return (fallbackSlug && categories.find((c) => c.slug === fallbackSlug)?.id) || null;
  }, [categoryChoice, task, categories]);

  const people: PeoplePickerProfile[] = useMemo(
    () =>
      (profilesQ.data ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .map((p) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
        })),
    [profilesQ.data],
  );

  const selectedCategory = categories.find((c) => c.id === categoryId) ?? null;
  const defaultAssignee = selectedCategory?.default_assignee_id
    ? (people.find((p) => p.id === selectedCategory.default_assignee_id) ?? null)
    : null;

  const canSubmit = !!task && !!entry && title.trim().length > 0 && !send.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !task || !entry) return;
    try {
      const ticket = await send.mutateAsync({
        task,
        entry,
        title,
        description,
        categoryId,
        priority,
        assigneeId: sendTo[0] ?? null,
        neededBy: neededBy || null,
      });
      const routedTo = ticket.assignee
        ? (ticket.assignee.full_name ?? ticket.assignee.email)
        : null;
      toast.success(
        routedTo
          ? `${ticketRef(ticket.number)} sent to ${routedTo}.`
          : `${ticketRef(ticket.number)} filed — a manager will route it.`,
      );
      onOpenChange(false);
      onSent?.(ticket.id);
    } catch (err) {
      toast.error(describeTicketError(err));
    }
  };

  const kindMeta = task ? FEEDBACK_TASK_KIND_META[task.kind] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={submit} className="min-w-0 space-y-5">
          <DialogHeader>
            <DialogTitle>Send request</DialogTitle>
            <DialogDescription>
              {entry ? (
                <>
                  Files this as a ticket on behalf of{' '}
                  <span className="font-medium text-foreground">
                    {feedbackCustomerLabel(entry)}
                  </span>
                  . You are the requester, so you will see every status change in your inbox.
                </>
              ) : (
                'Files this task as a ticket.'
              )}
            </DialogDescription>
          </DialogHeader>

          {kindMeta && (
            <div className="flex min-w-0 flex-wrap items-start gap-2 text-xs text-muted-foreground">
              <span
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 font-medium',
                  kindMeta.className,
                )}
              >
                <kindMeta.icon className="h-3 w-3" strokeWidth={1.8} />
                {kindMeta.label}
              </span>
              {task?.evidence && (
                <span className="line-clamp-2 min-w-0 flex-1 basis-48 break-words italic leading-5">
                  “{task.evidence}”
                </span>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Which team should this go to?</Label>
            <CategoryGrid
              categories={categories}
              value={categoryId}
              onChange={setCategoryChoice}
              loading={categoriesQ.isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="request-title">Title</Label>
            <Input
              id="request-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={160}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="request-description">Details</Label>
            <Textarea
              id="request-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[160px]"
            />
            <p className="text-xs text-muted-foreground">
              Written for someone who has not read the conversation — the customer, channel, your
              notes and the customer&apos;s own words are included. Trim anything they do not need.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger className="h-10 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden
                          className={cn('h-2 w-2 rounded-full', TICKET_PRIORITY_META[p].dot)}
                        />
                        {TICKET_PRIORITY_META[p].label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Needed by</Label>
              <DatePicker
                value={neededBy}
                onChange={setNeededBy}
                placeholder="No date"
                aria-label="Needed by"
                className="h-10 w-full"
                showShortcuts
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Send to</Label>
            <PeoplePicker
              profiles={people}
              value={sendTo}
              onChange={(next) => setSendTo(next.slice(-1))}
              placeholder={
                defaultAssignee
                  ? `Goes to ${defaultAssignee.full_name ?? defaultAssignee.email} unless you pick someone`
                  : 'Leave empty and a manager will route it'
              }
            />
          </div>

          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {send.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
